import { useAddressesManagement } from '../hooks/use-addresses-management';
import Icon from '../../../../components/icons/icon';
import { AccountAddressCard } from './account-address-card';
import { AccountAddressForm } from '../account-adress-form';
import { EcommerceButton } from '../../../../components/form/button';
import { Typography } from '../../../../components/typography/typography';
import { ConfirmModal } from '../../../../components/modal/confirm-modal';
import { UserAddress } from '../../../../clients/quipli-service/user/user';
import { Location } from '../../../../clients/quipli-service/location/location';
import SkeletonLoader from '../../../../components/skeleton-loader/skeleton-loader';

export const AccountAddresses = ({
  addresses,
  enableSelect,
  onSelect,
  onOpenForm,
  selectedAddress,
  type,
  reload,
  location,
}: {
  addresses: UserAddress[];
  enableSelect: boolean;
  onSelect?: (address?: UserAddress) => void;
  onOpenForm?: () => void;
  selectedAddress?: UserAddress;
  type: 'billing' | 'delivery';
  reload?: () => Promise<void>;
  location: Location;
}) => {
  const {
    isInitializing,
    isFormVisible,
    isLocationsModuleEnabled,
    deliveryLocations,
    deleteModalState,
    handleOpenForm,
    handleCloseForm,
    handleConfirmForm,
    openDeleteModal,
    closeDeleteModal,
    handleDeleteAddress,
    getFormAddress,
  } = useAddressesManagement({
    addresses,
    enableSelect,
    onSelect,
    onOpenForm,
    selectedAddress,
    type,
    reload,
  });

  // Show loading skeleton if the hooks are still initializing
  if (isInitializing) {
    return <SkeletonLoader num={2} direction="row" style={{ flex: '25%', height: '160px' }} />;
  }

  // Show form if we have an editable address (either real address or sentinel for new)
  if (isFormVisible) {
    return (
      <div data-cy="address-form-container">
        <AccountAddressForm
          address={getFormAddress()}
          type={type}
          enableDuplicate={type === 'delivery' || (type === 'billing' && location.allowDeliverySelection)}
          isLocationsModuleEnabled={isLocationsModuleEnabled}
          deliveryLocations={deliveryLocations}
          onConfirm={handleConfirmForm}
          onClose={handleCloseForm}
        />
      </div>
    );
  }

  // Show list view
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4 px-3">
        {addresses.map((address) => (
          <AccountAddressCard
            key={`address-${address.type}-${address.id}`}
            address={address}
            enableSelect={enableSelect}
            isSelected={selectedAddress?.id === address.id}
            onSelect={(value) => onSelect?.(value)}
            onEdit={(value) => {
              handleOpenForm(value);
            }}
            onDelete={() => openDeleteModal(address.id)}
            isLocationsModuleEnabled={isLocationsModuleEnabled}
          />
        ))}
      </div>

      <div className="flex justify-between items-center pb-3 px-3">
        <EcommerceButton
          buttonStyle="tertiary"
          onClick={() => {
            handleOpenForm();
          }}
          dataCy="add-new-address-button"
        >
          <Typography type="text-sm" weight="medium">
            Add New Address
          </Typography>
        </EcommerceButton>
      </div>

      <ConfirmModal
        useEcommerceTheme
        isOpen={deleteModalState.isOpen}
        close={closeDeleteModal}
        prompt="Delete Address"
        additionalText="Are you sure you want to delete this address?"
        headerIcon={<Icon icon="Trash" stroke="var(--error-500)" size="large" />}
        onConfirm={handleDeleteAddress}
      />
    </>
  );
};
